import { useCallback, useState } from "react";

type CopyFn = (text: string) => Promise<boolean>;

/**
 * Copy-to-clipboard with feedback state. Returns:
 *   - `copy(text)` — async, resolves true on success
 *   - `copiedText` — the last successfully copied string (or null)
 *   - `isSupported` — boolean for whether the Clipboard API is available
 *
 *   const { copy, copiedText } = useCopyToClipboard();
 *   <Button onClick={() => copy(snippet)}>{copiedText ? "Copied" : "Copy"}</Button>
 *
 * Adapted from usehooks-ts (MIT). Falls back gracefully when the Clipboard API
 * isn't available (older browsers, insecure contexts).
 */
export function useCopyToClipboard(): {
  copy: CopyFn;
  copiedText: string | null;
  isSupported: boolean;
} {
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const isSupported =
    typeof navigator !== "undefined" && Boolean(navigator.clipboard?.writeText);

  const copy: CopyFn = useCallback(
    async (text) => {
      if (!isSupported) {
        setCopiedText(null);
        return false;
      }
      try {
        await navigator.clipboard.writeText(text);
        setCopiedText(text);
        return true;
      } catch {
        setCopiedText(null);
        return false;
      }
    },
    [isSupported],
  );

  return { copy, copiedText, isSupported };
}
